import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";

const seasons = [
    { name: "Spring", images: ["spring4.png", "spring2.png", "spring3.png"] },
    { name: "Summer", images: ["summer1.png", "summer2.png", "summer3.png"] },
    { name: "Rainy", images: ["rainy1.png", "rainy2.png", "rainy3.png", "spring1.png"] },
    { name: "Winter", images: ["winter1.png", "winter2.png", "winter3.png"] },
];

const getRandomUniqueOptions = (correctOption: { name: any; images?: string[]; }) => {
    const options = [correctOption];
    const availableOptions = seasons.filter(season => season.name !== correctOption.name);
    while (options.length < 4) {
        const randomIndex = Math.floor(Math.random() * availableOptions.length);
        options.push(availableOptions[randomIndex]);
        availableOptions.splice(randomIndex, 1);
    }
    return options.sort(() => Math.random() - 0.5);
};

const GuessSeasonQuiz = () => {
    const getNewQuestion = () => {
        document.title = "Guess the Season – Nursery Worksheet | Mama’s Guidance";
        const newQuestion = seasons[Math.floor(Math.random() * seasons.length)];
        const randomImage = newQuestion.images[Math.floor(Math.random() * newQuestion.images.length)];
        return { newQuestion, newImage: randomImage, newOptions: getRandomUniqueOptions(newQuestion) };
    };

    const [{ newQuestion: question, newImage: image, newOptions: options }, setQuizState] = useState(getNewQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const handleAnswer = (selected: string) => {
        if (selected === question.name) {
            nextQuestion();
        } else {
            setFeedback("❌ Try Again!");
        }
    };

    const nextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {

            setQuizState(getNewQuestion());
            setFeedback("");
            setShowImage(false);

        }, 5000)
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Introduce nursery kids to weather and climate with the "Guess the Season" activity on Mamas Guidance—an interactive learning game to help children identify seasons based on clues, visuals, and everyday experiences.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord matchImage match-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Guess the Season</h1>

                                    <p>
                                        Teach your little one about the changing weather with our fun-filled <strong>Guess the Season</strong> worksheet! This colorful and interactive activity helps nursery children <strong>identify seasons based on clues, pictures, or familiar objects</strong>.
                                    </p>

                                    <h2>🌦️ What’s in the Worksheet?</h2>
                                    <p>
                                        The worksheet includes visual prompts and simple questions like <strong>“Which season do we wear woollen clothes in?”</strong> or <strong>“When do flowers bloom?”</strong> Students will guess between <b><i>Summer, Winter, Rainy, and Spring</i></b> seasons using logical observation.
                                    </p>

                                    <h2>🎯 What Your Child Will Learn</h2>
                                    <ul>
                                        <li>Recognize the four main seasons: <strong>Summer, Winter, Rainy, and Spring</strong></li>
                                        <li>Understand seasonal changes in clothes, weather, and nature</li>
                                        <li>Improve reasoning, memory, and observation</li>
                                        <li>Connect real-life experiences with classroom learning</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Ask the question or read the sentence aloud.</li>
                                        <li>Let your child look at the picture or think of a related experience.</li>
                                        <li>Circle, color, or tick the correct season.</li>
                                        <li>Talk about what they like doing in each season to reinforce learning.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/environmental-science/animal/match-animal"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Animal</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/environmental-science/fruit/match-fruit"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Fruit</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/environmental-science/body-parts/match-bodyparts"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Body Parts</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Is Important</h2>
                                    <p>
                                        Early knowledge of seasons builds a child’s <strong>understanding of the world around them</strong>. It also supports their <strong>awareness of time, clothing, weather, and environmental changes</strong> — essential parts of EVS learning.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Guess the Season</h1>

                                <p>
                                    Teach your little one about the changing weather with our fun-filled <strong>Guess the Season</strong> worksheet! This colorful and interactive activity helps nursery children <strong>identify seasons based on clues, pictures, or familiar objects</strong>.
                                </p>

                                <h2>🌦️ What’s in the Worksheet?</h2>
                                <p>
                                    The worksheet includes visual prompts and simple questions like “Which season do we wear woollen clothes in?” or “When do flowers bloom?” Students will guess between <em>Summer, Winter, Rainy, and Spring</em> seasons using logical observation.
                                </p>

                                <h2>🎯 What Your Child Will Learn</h2>
                                <ul>
                                    <li>Recognize the four main seasons: Summer, Winter, Rainy, and Spring</li>
                                    <li>Understand seasonal changes in clothes, weather, and nature</li>
                                    <li>Improve reasoning, memory, and observation</li>
                                    <li>Connect real-life experiences with classroom learning</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Ask the question or read the sentence aloud.</li>
                                    <li>Let your child look at the picture or think of a related experience.</li>
                                    <li>Circle, color, or tick the correct season.</li>
                                    <li>Talk about what they like doing in each season to reinforce learning.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/environmental-science/animal/match-animal"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Animal</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/fruit/match-fruit"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Fruit</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/body-parts/match-bodyparts"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Body Parts</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Important</h2>
                                <p>
                                    Early knowledge of seasons builds a child’s understanding of the world around them. It also supports their awareness of time, clothing, weather, and environmental changes — essential parts of EVS learning.
                                </p>
                            </article>
                            <img style={{ height: '50%' }} src={process.env.REACT_APP_API_URL + image} className="img-thumbnail season-image" />
                            <div className="mt-4" style={{ display: 'ruby' }}>
                                {options.map((option, index) => (
                                    <div key={index} style={{ margin: '10px' }}>
                                        <button className="optionButton background-blue" onClick={() => handleAnswer(option.name)}>{option.name}</button>
                                    </div>
                                ))}
                            </div>
                            <h3>{feedback}</h3>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_LOADING_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default GuessSeasonQuiz;
